<template>
  <div class="w-full bg-white overflow-x-hidden">
    <!-- Hero Section -->
    <section class="min-h-[40vh] relative bg-gradient-to-r from-[#0D0971] to-[#1a1a8e]">
      <div class="absolute inset-0 bg-black/20"></div>
      <div class="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 py-12">
        <h1 class="font-bold text-3xl text-white mb-4">
          {{ categoryData?.title || 'Category Details' }}
        </h1>
        <div class="w-24 h-1 bg-white/50 mb-6"></div>
        <button
          @click="goBack"
          class="mt-4 px-6 py-3 bg-white text-[#0D0971] font-semibold text-base rounded-lg hover:bg-gray-100 transition-colors duration-300 flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>

    <!-- Content Section -->
    <section class="pt-8 pb-4">
      <div class="px-6">
        <!-- Description -->
        <div class="mb-8">
          <div v-if="Array.isArray(categoryData?.description)" class="space-y-4">
            <p v-for="(paragraph, index) in categoryData.description"
               :key="index"
               class="text-sm text-[#444] leading-relaxed">
              {{ paragraph }}
            </p>
          </div>
          <p v-else class="text-sm text-[#444] leading-relaxed">
            {{ categoryData?.description }}
          </p>
        </div>

        <!-- Applications -->
        <div v-if="categoryData?.applications" class="mb-8">
          <h2 class="text-xl font-bold text-[#0D0971] mb-4 flex items-center gap-2">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
            Applications
          </h2>
          <div v-if="Array.isArray(categoryData.applications)" class="bg-white rounded-lg shadow-md p-5 border border-gray-100">
            <ul class="space-y-3">
              <li v-for="(app, index) in categoryData.applications"
                  :key="index"
                  class="flex items-start gap-2 text-sm text-[#444]">
                <span class="text-[#0D0971] font-bold mt-0.5">•</span>
                <span>{{ app }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Certifications -->
        <div v-if="categoryData?.certifications" class="mb-8">
          <h2 class="text-xl font-bold text-[#0D0971] mb-4 flex items-center gap-2">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
            Certifications
          </h2>
          <div class="bg-white rounded-lg shadow-md p-5 border border-gray-100">
            <ul class="space-y-3">
              <li v-for="(cert, index) in categoryData.certifications"
                  :key="index"
                  class="flex items-start gap-2 text-sm text-[#444]">
                <svg class="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                </svg>
                <span>{{ cert }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Product Introductions -->
        <div v-if="categoryData?.productIntroductions" class="mt-8">
          <h2 class="text-2xl font-bold text-[#0D0971] mb-6 flex items-center gap-2">
            <span class="text-xl">1 |</span>
            {{ categoryData.productIntroductions.title }}
          </h2>

          <!-- Products List -->
          <div v-for="(product, index) in categoryData.productIntroductions.products" :key="index" class="mb-8">
            <!-- Product Name -->
            <h3 class="text-lg font-bold text-[#0D0971] mb-4">{{ product.name }}</h3>

            <!-- Features and Photos Table -->
            <div class="-mx-6 px-6">
              <table class="w-full border-collapse table-fixed">
                <thead>
                  <tr class="bg-[#0D0971] text-white">
                    <th class="text-sm font-bold p-3 text-center border border-[#0D0971] w-1/2">
                      {{ t('categoryDetail.tableHeaders.features') }}
                    </th>
                    <th class="text-sm font-bold p-3 text-center border border-[#0D0971] w-1/2">
                      {{ t('categoryDetail.tableHeaders.photos') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <!-- Features Column -->
                    <td class="p-4 bg-white border border-gray-300 align-top">
                      <ul class="space-y-2">
                        <li v-for="(feature, fIndex) in product.features"
                            :key="fIndex"
                            class="flex items-start gap-2 text-sm text-[#444]">
                          <span class="text-[#0D0971] font-bold mt-0.5">•</span>
                          <span>{{ feature }}</span>
                        </li>
                      </ul>
                    </td>
                    <!-- Photos Column -->
                    <td class="p-3 bg-gray-50 border border-gray-300 align-top">
                      <div class="space-y-3">
                        <div v-for="(photo, pIndex) in product.photos"
                             :key="pIndex"
                             class="bg-white rounded-lg overflow-hidden shadow-sm">
                          <img :src="photo"
                               :alt="`${product.name} photo ${pIndex + 1}`"
                               class="w-full h-auto object-cover"
                               @error="handleImageError">
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Solutions - Custom Fabrication -->
        <div v-if="categoryData?.solutionsCustomFabrication" class="mt-8">
          <h2 class="text-2xl font-bold text-[#0D0971] mb-4 flex items-center gap-2">
            <span class="text-xl">2 |</span>
            {{ categoryData.solutionsCustomFabrication.title }}
          </h2>

          <!-- Description -->
          <p class="text-sm text-[#444] mb-6">
            {{ categoryData.solutionsCustomFabrication.description }}
          </p>

          <!-- Solutions Table -->
          <div class="-mx-6 px-6">
            <table class="w-full border-collapse table-fixed">
              <thead>
                <tr class="bg-[#0D0971] text-white">
                  <th class="text-sm font-bold p-3 text-left border border-[#0D0971] w-[35%]">
                    {{ t('categoryDetail.tableHeaders.solution') }}
                  </th>
                  <th class="text-sm font-bold p-3 text-left border border-[#0D0971] w-[65%]">
                    {{ t('categoryDetail.tableHeaders.introductoryText') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in categoryData.solutionsCustomFabrication.solutions"
                    :key="index"
                    :class="index % 2 === 0 ? 'bg-[#f3f8ff]' : 'bg-white'">
                  <td class="text-sm font-semibold text-[#0D0971] p-3 border border-gray-300 align-top break-words">
                    {{ item.solution }}
                  </td>
                  <td class="text-sm text-[#444] p-3 border border-gray-300 align-top break-words">
                    <ul class="space-y-2">
                      <li v-for="(intro, iIndex) in item.introduction"
                          :key="iIndex"
                          class="flex items-start gap-2">
                        <span class="text-[#0D0971] font-bold mt-0.5">•</span>
                        <span>{{ intro }}</span>
                      </li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Sections (Partner Strengths & Client Benefits) -->
        <div v-if="categoryData?.sections && categoryData.sections.length > 0">
          <div v-for="(section, sectionIndex) in categoryData.sections" :key="sectionIndex" class="mb-8 mt-8">
            <!-- Section Title -->
            <h2 class="text-2xl font-bold text-[#0D0971] mb-6 flex items-center gap-2">
              <span class="text-xl">{{ sectionIndex + 3 }} |</span>
              {{ section.title }}
            </h2>

            <!-- Section Items -->
            <div v-if="section.items && section.items.length > 0" class="space-y-4">
              <div v-for="(item, index) in section.items" :key="index"
                   class="bg-gradient-to-br from-[#f3F8FF] to-[#e8f0fe] rounded-lg p-5 border-l-4 border-[#0D0971]">
                <!-- Item Name -->
                <h3 class="text-base font-bold text-[#0D0971]" :class="{ 'mb-2': item.description }">{{ item.name }}</h3>

                <!-- Item Description (only if exists) -->
                <p v-if="item.description" class="text-sm text-[#444] leading-relaxed">
                  {{ item.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Image Gallery -->
    <section class="bg-[#F3F8FF]">
      <ImageGalleryForMobile :imageItems="beltConveyorGallery" :imagesPerPage="4" :columns="1" aspectRatio="4 / 3"/>
    </section>
   

    <!-- Category Not Found -->
    <section v-if="!categoryData" class="py-16">
      <div class="px-6 text-center">
        <h2 class="text-2xl font-bold text-[#0D0971] mb-4">{{ t('categoryDetail.categoryNotFound') }}</h2>
        <p class="text-base text-[#666] mb-6">
          {{ t('categoryDetail.categoryNotFoundMessage') }}
        </p>
        <button
          @click="goBack"
          class="px-6 py-3 bg-[#0D0971] text-white font-semibold text-base rounded-lg hover:bg-[#1a1a8e] transition-colors duration-300">
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import { pushToDataLayer } from '../utils/gtm';
import { useChatWidget } from '../utils/chatWidget';
import ImageGalleryForMobile from "../components/ImageGalleryForMobile.vue";
import { beltConveyorGallery } from "../utils/imageData";

const router = useRouter();
const { locale, messages, t } = useI18n();

// Get Belt Conveyor category data from i18n (hardcoded to 'belt-conveyor' category)
const categoryData = computed(() => {
  const categories = messages.value[locale.value]?.sourceProduct?.categorys || [];
  const beltConveyorCategory = categories.find((cat: any) => cat.id === 'belt-conveyor');
  return beltConveyorCategory?.detailContent || null;
});

onMounted(() => {
  pushToDataLayer('page_view', {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
  });
});

const goBack = () => {
  router.push('/core-offers/source-product');
  pushToDataLayer('click', {
    category: 'MobileCategoryBeltConveyorDetail',
    label: 'Back to Source Product',
  });
};

const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("MobileCategoryBeltConveyorDetail");
  pushToDataLayer('click', {
    category: 'MobileCategoryBeltConveyorDetail',
    label: 'Contact Us',
  });
};

// Handle image loading errors
const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement;
  // Prevent infinite loop by checking if already tried to load fallback
  if (!target.dataset.errorHandled) {
    target.dataset.errorHandled = 'true';
    // Use a data URL as fallback to avoid another network request
    target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="18" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EImage not available%3C/text%3E%3C/svg%3E';
  }
};

</script>

